import React from 'react';
import styles from '../QuoteTable/QuoteTable.module.scss';
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton, FormLabel } from '@mui/material';
import CloseIcon from "@mui/icons-material/Close";
import Button from "@mui/material/Button";
import { useTranslation } from 'react-i18next';
import { FormTextField } from '../../../components/ReactHookForm/ReactHookForm';
import { useForm } from 'react-hook-form';
import { checkOnlySpace } from '../../../common/Utils/CommonFunctions/CommonFunctions';

const QuoteApproveAlert = ({ isOpen, handleCancel, handleApprovalSubmit, quoteItemList, dxCode, authCode, isSubmitted }) => {
  const { t } = useTranslation();
  const { control, getFieldState, handleSubmit, trigger, formState: { errors } } = useForm({
    defaultValues: {
      authCode: authCode?.value ?? "",
      dxCode: dxCode?.value ?? ""
    },
    mode: "all"
  });
  const checkedItems = quoteItemList?.filter((item) => item?.enabled);
  const deniedItems = quoteItemList?.filter((item) => !item?.enabled)
  const total = checkedItems?.reduce((sum, row) => {
    return sum + (row?.client_price * row?.quantity);
  }, 0);

  const handleApproveAlertSubmit = () => {
    handleSubmit(handleApproveAlertSuccess, handleApproveAlertError)();
  }
  const handleApproveAlertSuccess = (data) => {
    handleApprovalSubmit(data?.dxCode, data?.authCode)
  }
  const handleApproveAlertError = () => {
    trigger(null, { shouldFocus: true });
  }

  return (
    <div className={styles.QuoteApproveAlert} data-testid="QuoteApproveAlert">
      <Dialog open={isOpen} onClose={handleCancel}
        sx={{
          "& .MuiDialog-container": {
            "& .MuiPaper-root": {
              width: "100%",
              maxWidth: "400px",
            },
          },
        }}
      >
        <DialogTitle className={styles.commonDialogTitle}>
          <div className={styles.dialogTitleContainer}>
            <span className={styles.commonFexContainer}>{t("Quote.approveQuote")}</span>
            <IconButton
              className={styles.cancelIcon}
              onClick={handleCancel}
              aria-label="close"
              data-testid="closeAddSupportPin"
            >
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent className={`${styles.paddedBox} ${styles.marginTop20px}`}>
          <p className={styles.dialogDescription}>
            <span>{t("Quote.approveThisQuoteAlertMessage")}</span>
          </p>
          <p className={`${styles.dialogDescription} ${styles.underLineItem} ${styles.marginTop20px}`}>
            <span>{t("Quote.itemsApproved")}</span>
          </p>
          {checkedItems?.map((item, index) => (
            <p key={index} className={`${styles.dialogDescription} marginTopFive`}>
              <span className={styles.inlineFlexCenterGap}>
                {item?.description} <CloseIcon fontSize="small" /> {item?.quantity}
              </span>
            </p>
          ))}

          <p className={`${styles.dialogDescription} ${styles.underLineItem} ${styles.marginTop20px}`}>
            <span>{t("Quote.itemsDenied")}</span>
          </p>
          {deniedItems && deniedItems?.length > 0 ? (
            deniedItems?.map((item, index) => (
              <p key={index} className={`${styles.dialogDescription} marginTopEight`}>
                <span className={styles.inlineFlexCenterGap}>
                  {item?.description} <CloseIcon fontSize="small" /> {item?.quantity}
                </span>
              </p>
            ))
          ) : (
            <p className={`${styles.dialogDescription} marginTop10px`}>-</p>
          )}
          <p className={`${styles.dialogDescription} ${styles.marginTop20px}`}>
            <span>{t("reports.total")}</span>
            <span> ${total?.toFixed(2) ?? 0}</span>
          </p>
          {authCode && <>
            <p className={`${styles.dialogDescription} ${styles.marginTop20px} marginBottomFive`}>
              {authCode?.display_name} {authCode?.is_required && <span className="asteriskSignColor"> *</span>}
            </p>
            <div>
              <FormTextField maxLength={authCode?.field_length} name="authCode" control={control} sx={{ mt: { xs: 0, sm: 1 } }} rules={authCode?.is_required ? { required: true, validate: { isOnlySpaces: checkOnlySpace }} : {validate: { isOnlySpaces: checkOnlySpace }}} fullWidth variant="outlined" size="small" />
              {(getFieldState("authCode").isTouched || getFieldState("authCode").isDirty || getFieldState("authCode").invalid) && errors.authCode?.type == "required" && <FormLabel error>{t("AddPatient.fieldRequired")}</FormLabel>}
            </div>
          </>}
          {dxCode && <>
            <p className={`${styles.dialogDescription} ${styles.marginTop20px} marginBottomFive`}>
              {dxCode?.display_name} {dxCode?.is_required && <span className="asteriskSignColor"> *</span>}
            </p>
            <div>
              <FormTextField maxLength={dxCode?.field_length} name="dxCode" control={control} sx={{ mt: { xs: 0, sm: 1 } }} rules={dxCode?.is_required ? { required: true, validate: { isOnlySpaces: checkOnlySpace } } : {validate: { isOnlySpaces: checkOnlySpace }}} fullWidth variant="outlined" size="small" />
              {(getFieldState("dxCode").isTouched || getFieldState("dxCode").isDirty || getFieldState("dxCode").invalid) && errors.dxCode?.type == "required" && <FormLabel error>{t("AddPatient.fieldRequired")}</FormLabel>}
            </div>
          </>}
        </DialogContent>
        <DialogActions className='alertButton'>
          <Button
            onClick={handleCancel}
            color='secondary' size="small">
            {t("Prescriptions.cancel")}
          </Button>
          <Button
            onClick={handleApproveAlertSubmit}
            data-testid="confirmButton"
            disabled={isSubmitted}
            color='primary' size="small">
            {t("NewOrder.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
};

export default QuoteApproveAlert;